import React, { Component } from 'react';
import { Row, Col, Button, Modal, Form, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { GoBrowser, GoTable } from 'react-icons/go';
import Select from 'components/controls/select';
import { definitionAliasError } from "context/workflowReference";

const layoutOptions = [
  { name: "Free-form", value: "free_form", icon: <GoBrowser /> },
  { name: "Grid",      value: "grid",      icon: <GoTable /> },
];

const verticalSizingOptions = [
  { value: "pitch",      label: "Fixed pitch" },
  { value: "fit",        label: "Fit to viewport" },
  { value: "fixed_rows", label: "Fixed rows per screen" },
];

const DEFAULT_LAYOUT_STATE = {
  layoutType: "free_form",
  columns: 12,
  rowHeight: 30,
  verticalSizing: "pitch",
  referenceRows: 12,
};

export default class AddDefinitionModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      definition: {
        id: null,
        alias: ""
      },
      aliasError: null,
      ...DEFAULT_LAYOUT_STATE
    };

    this.form = React.createRef();
  }

  onEnter = () => {
    if ( this.props.definition ) {
      this.setState({
        definition: {
          id: this.props.definition.id,
          alias: this.props.definition.alias
        },
        aliasError: null
      });
    } else {
      this.setState({
        definition: {
          id: null,
          alias: ""
        },
        aliasError: null,
        ...DEFAULT_LAYOUT_STATE
      });
    }
  }

  handleAddDefinition = () => {
    // Check form validity
    if ( !this.form.current.reportValidity() ) {
      return;
    }

    const aliasError = definitionAliasError(this.state.definition.alias);
    if ( aliasError ) {
      this.setState({aliasError: aliasError});
      return;
    }

    // Initiate processing
    if ( this.props.definition ) {
      this.props.handleEditDefinition(this.state.definition);
    } else {
      this.props.handleAddDefinition({
        id: null,
        alias: this.state.definition.alias,
        layoutType: this.state.layoutType,
        columns: this.state.columns,
        rowHeight: this.state.rowHeight,
        verticalSizing: this.state.verticalSizing,
        referenceRows: this.state.referenceRows,
      });
    }

    // Close modal
    this.props.onHide();
  }

  render() {
    return (
      <Modal
        {...this.props}
        onEnter={this.onEnter}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {this.props.definition
              ? <>Edit Playground Definition</>
              : <>Add New Playground Definition</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form ref={this.form} onSubmit={(event) => {
            event.preventDefault();
            this.handleAddDefinition();
          }}>
            <Form.Group>
              {this.props.definition &&
                <>
                  <Form.Text>
                    <strong>Playground Definition ID</strong>
                  </Form.Text>
                  <Form.Text>
                    <code>{this.props.definition.id}</code>
                  </Form.Text>
                  <hr />
                </>
              }
              <Form.Label>
                <strong>Alias</strong> <span className="text-muted">(required)</span>
              </Form.Label>
              <Row>
                <Col sm={3}>
                  <Form.Control
                    required
                    type="text"
                    size="sm"
                    autoFocus
                    value={this.state.definition.alias}
                    onChange={(event) => {
                      let definition = this.state.definition;
                      definition.alias = event.target.value;
                      this.setState({definition: definition, aliasError: null});
                    }}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                Playground alias must confirm to the following requirements:
                <ul>
                  <li>minimum of 6 characters;</li>
                  <li>maximum of 42 characters;</li>
                  <li>only alphanumeric characters along with <strong>-</strong>,<strong>_</strong> are allowed;</li>
                  <li>must be unique within the scope of the revision.</li>
                </ul>
              </Form.Text>
              <Form.Control.Feedback type="invalid"
                style={{"display": this.state.aliasError ? "block" : "none"}}>
                {this.state.aliasError}
              </Form.Control.Feedback>
            </Form.Group>

            {!this.props.definition && (
              <>
                <hr />
                <Form.Group>
                  <Form.Label><strong>Layout Type</strong></Form.Label>
                  <div>
                    <ButtonGroup toggle size="sm" className="playground-layout-toggle">
                      {layoutOptions.map((option, idx) => (
                        <ToggleButton
                          key={idx}
                          id={`playground-layout-${idx}`}
                          type="radio"
                          variant="light"
                          name="layout-type"
                          value={option.value}
                          checked={this.state.layoutType === option.value}
                          onChange={() => this.setState({ layoutType: option.value })}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            {option.icon} {option.name}
                          </span>
                        </ToggleButton>
                      ))}
                    </ButtonGroup>
                  </div>
                  <Form.Text muted>
                    <div>
                      <strong>Free-form</strong> — widgets are placed at absolute pixel coordinates on
                      an open canvas. Each widget carries its own width and height, and can be moved
                      and resized freely.
                    </div>
                    <div style={{ marginTop: "6px" }}>
                      <strong>Grid</strong> — widgets snap to a fixed-column grid; positions and sizes
                      are expressed in grid units. Best for dashboard-style layouts that scale predictably
                      with the available space.
                    </div>
                  </Form.Text>
                </Form.Group>

                {this.state.layoutType === "grid" && (
                  <>
                    <hr />
                    <Form.Group>
                      <Form.Label><strong>Columns</strong></Form.Label>
                      <Row>
                        <Col sm={3}>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={4}
                            max={128}
                            value={this.state.columns}
                            onChange={(e) => this.setState({ columns: parseInt(e.target.value, 10) || 12 })}
                          />
                        </Col>
                      </Row>
                      <Form.Text muted>
                        Number of vertical columns the canvas is divided into. Higher values give finer
                        horizontal control over widget placement and width. Range: 4–128.
                      </Form.Text>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label><strong>Row Height</strong> <span className="text-muted">(pixels)</span></Form.Label>
                      <Row>
                        <Col sm={3}>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={10}
                            max={200}
                            value={this.state.rowHeight}
                            onChange={(e) => this.setState({ rowHeight: parseInt(e.target.value, 10) || 30 })}
                          />
                        </Col>
                      </Row>
                      <Form.Text muted>
                        Height of a single grid row in pixels. Smaller values give finer vertical control
                        over widget placement and height. Range: 10–200. Applies to pitch mode.
                      </Form.Text>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label><strong>Vertical Sizing</strong></Form.Label>
                      <Row>
                        <Col sm={6}>
                          <Select
                            options={verticalSizingOptions}
                            value={verticalSizingOptions.find((o) => o.value === this.state.verticalSizing) || verticalSizingOptions[0]}
                            onChange={(option) => this.setState({ verticalSizing: option.value })}
                          />
                        </Col>
                      </Row>
                      <Form.Text muted>
                        Controls how the grid fills the vertical space of the viewport:
                        <ul>
                          <li><strong>Fixed pitch</strong> — rows keep a set pixel height; content taller than the viewport scrolls.</li>
                          <li><strong>Fit to viewport</strong> — all widgets scale so everything fits the height; adding widgets shrinks them.</li>
                          <li><strong>Fixed rows per screen</strong> — a set number of rows always fills the height; content beyond that scrolls.</li>
                        </ul>
                      </Form.Text>
                    </Form.Group>
                    {this.state.verticalSizing === "fixed_rows" && (
                      <Form.Group>
                        <Form.Label><strong>Reference Rows</strong></Form.Label>
                        <Row>
                          <Col sm={3}>
                            <Form.Control
                              type="number"
                              size="sm"
                              min={1}
                              max={128}
                              value={this.state.referenceRows}
                              onChange={(e) => this.setState({ referenceRows: parseInt(e.target.value, 10) || 12 })}
                            />
                          </Col>
                        </Row>
                        <Form.Text muted>
                          Number of rows that fill the viewport height.
                        </Form.Text>
                      </Form.Group>
                    )}
                  </>
                )}
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          <Button variant="success" onClick={this.handleAddDefinition}>
            {this.props.definition ? "Apply" : "Add"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
